import { ReactNode } from "react";
import { ColumnConfig, ViewConfig } from "@lib/views";
import { Skeleton } from "@design-system/Skeleton/Skeleton";
import { GroupByRow } from "./groupByRowTypes";
import { getSkeletonBarWidth } from "../getSkeletonBarWidth";
import { GroupHeaderContent } from "./GroupRowContent";

/**
 * Lightweight item-column descriptor. Cells receive the item directly via
 * `renderItem(item)` rather than a `CellContext<TItem>`, so wrapping into the
 * `GroupByRow` config needs no cast/re-typing of TanStack's context (the known
 * failure mode when reusing a full item `ViewConfig`).
 */
export interface GroupByColumnDef<
    TItem,
    TColumn extends string,
    TSort extends string = never,
> {
    id: TColumn;
    header: string;
    /**
     * Custom header renderer, for a column whose values need the label
     * positioned to match them rather than sitting at the cell's edge.
     */
    headerCell?: ColumnConfig<
        GroupByRow<TItem>,
        undefined,
        TSort
    >["headerCell"];
    name?: string;
    minSize: number;
    maxSize: number;
    /** When true, this column hosts the chevron + group-header content. */
    isExpandColumn?: boolean;
    /**
     * Blocks the user from reordering, hiding or unpinning the column. The
     * expand column is always immutable regardless of this flag.
     */
    immutable?: boolean;
    /** Keeps the column out of the column-settings lists entirely. */
    hiddenFromSettings?: boolean;
    align?: "right";
    sort?: {
        asc: TSort;
        desc: TSort;
        defaultOrder?: "asc" | "desc";
    };
    /**
     * Whether loading rows render a shimmer bar in this column. Opt out for
     * columns holding controls rather than data — a fake bar where an action
     * button will appear reads as content that never arrives.
     */
    hasSkeleton?: boolean;
    renderItem: (item: TItem) => ReactNode;
}

interface BuildGroupByViewConfigParams<
    TItem,
    TColumn extends string,
    TSort extends string,
> {
    columns: Array<GroupByColumnDef<TItem, TColumn, TSort>>;
}

/**
 * Narrows the reduce-built partial record to a complete one. The runtime check
 * mirrors what the types can't prove: one config entry exists per column id.
 */
function isCompleteColumnRecord<TColumn extends string, TValue>(
    record: Partial<Record<TColumn, TValue>>,
    columnIds: TColumn[],
): record is Record<TColumn, TValue> {
    return columnIds.every((columnId) => record[columnId] !== undefined);
}

interface RenderGroupByCellParams<
    TItem,
    TColumn extends string,
    TSort extends string,
> {
    groupRow: GroupByRow<TItem>;
    rowIndex: number;
    column: GroupByColumnDef<TItem, TColumn, TSort>;
    columnIndex: number;
    expandColumnId: TColumn;
}

export function renderGroupByCell<
    TItem,
    TColumn extends string,
    TSort extends string = never,
>({
    groupRow,
    rowIndex,
    column,
    columnIndex,
    expandColumnId,
}: RenderGroupByCellParams<TItem, TColumn, TSort>): ReactNode {
    if (groupRow.rowType === "item") {
        return column.renderItem(groupRow.item);
    }
    if (groupRow.rowType === "skeleton") {
        if (column.hasSkeleton === false) {
            return null;
        }
        const width = getSkeletonBarWidth({
            minSize: column.minSize,
            columnIndex,
            rowIndex,
        });
        return <Skeleton width={width} height={16} />;
    }
    if (groupRow.rowType === "groupHeader" && column.id === expandColumnId) {
        return <GroupHeaderContent descriptor={groupRow.descriptor} />;
    }
    return null;
}

/**
 * Turns item-column descriptors into a `ViewConfig<GroupByRow<TItem>>` whose
 * every cell dispatches on `row.rowType`: item rows delegate to `renderItem`,
 * skeleton rows render a per-column shimmer bar, the expand column renders the
 * group header for group rows, everything else renders nothing (load-more and
 * load-error rows render full-width via `renderFullWidthRow`).
 */
export function buildGroupByViewConfig<
    TItem,
    TColumn extends string,
    TSort extends string = never,
>({
    columns,
}: BuildGroupByViewConfigParams<TItem, TColumn, TSort>): ViewConfig<
    GroupByRow<TItem>,
    TColumn,
    TSort
> {
    const [firstColumn] = columns;
    if (!firstColumn) {
        throw new Error("buildGroupByViewConfig requires at least one column");
    }
    const expandColumn =
        columns.find((column) => column.isExpandColumn) ?? firstColumn;

    const columnConfigs = columns.reduce<
        Partial<
            Record<TColumn, ColumnConfig<GroupByRow<TItem>, undefined, TSort>>
        >
    >((acc, column, columnIndex) => {
        acc[column.id] = {
            accessorKey: column.id,
            name: column.name ?? column.header,
            header: column.header,
            headerCell: column.headerCell,
            minSize: column.minSize,
            maxSize: column.maxSize,
            // The expand column hosts the chevron and group header content —
            // hiding it would strip every group label from the table, so it
            // stays immutable even if a caller passes `immutable: false`.
            immutable:
                column.id === expandColumn.id || (column.immutable ?? false),
            hiddenFromSettings: column.hiddenFromSettings,
            align: column.align,
            sort: column.sort,
            cell: ({ row }) =>
                renderGroupByCell({
                    groupRow: row.original,
                    rowIndex: row.index,
                    column,
                    columnIndex,
                    expandColumnId: expandColumn.id,
                }),
        };
        return acc;
    }, {});

    if (
        !isCompleteColumnRecord(
            columnConfigs,
            columns.map((column) => column.id),
        )
    ) {
        throw new Error("buildGroupByViewConfig: missing column config");
    }

    return {
        columns: columnConfigs,
        nesting: {
            enabled: true,
            expandColumn: expandColumn.id,
            childrenKey: "children",
        },
    };
}
